// Ekrany kont: logowanie rodziny (PIN rodzica), „Kto dziś się uczy?”, nowy profil (pierwszy i kolejne), brak internetu, „Mój wygląd”.
// Rodzic loguje urządzenie PIN-em raz, a potem dziecko przy każdym uruchomieniu wybiera swój profil (jak w Netflixie); profil z PIN-em dziecka pyta o niego (SPEC §15, §19).
// Napisy idą za przełącznikiem EN/PL (patch-33). Świeże urządzenie startuje po angielsku, a rodzic po polsku musi móc to zmienić, więc ekrany logowania,
// pierwszego profilu i „brak internetu” mają w rogu przełącznik języka. Zostają po polsku: blok „Dla rodzica” na ekranie wyboru profilu, nazwa urządzenia
// podpowiadana w polu (to wartość dla panelu rodzica) i komunikaty błędów z panelu rodzica (`r.message`).
import { createAdminClient } from '../lib/adminApi.ts';
import type { AdminClient } from '../lib/adminApi.ts';
import type { Accounts, Entry } from '../lib/accounts.ts';
import { pairFailureText } from '../lib/device.ts';
import type { PairResult } from '../lib/device.ts';
import { guessDeviceName } from '../lib/deviceName.ts';
import type { Pair } from '../lib/i18n.ts';
import { PROFILE_NAME_MAX } from '../lib/look.ts';
import { PIN_LENGTH, isPin } from '../lib/pinLocal.ts';
import type { Look } from '../lib/look.ts';
import type { ProfileInfo } from '../lib/profilesApi.ts';
import { renderAdmin } from './admin.ts';
import { avatarEl } from './avatars.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import type { Child } from './dom.ts';
import { createGenderPicker } from './genderPicker.ts';
import { langOf, tr, tx, txAttr, txText } from './i18n.ts';
import { liveStatus } from './i18nLive.ts';
import { icon, mascot } from './kit.ts';
import type { Pose } from './kit.ts';
import { renderLangCorner } from './langToggle.ts';
import { LOGIN_SCREEN } from '../lib/screens.ts';
import { underlinedTitle } from './welcome.ts';
import { createLookPicker } from './lookPicker.ts';
import { openParentGate } from './parentGate.ts';
import { openPinPad } from './pinPad.ts';
import { applyLook } from './theme.ts';

const T = {
  connecting: { en: 'Connecting to the server…', pl: 'Łączę z serwerem…' },
  // logowanie rodzica
  pin: { en: 'Parent PIN', pl: 'PIN rodzica' },
  deviceName: { en: 'Device name', pl: 'Nazwa urządzenia' },
  deviceNameHint: { en: "Device name, e.g. Ania's tablet", pl: 'Nazwa urządzenia, np. Tablet Ani' },
  logIn: { en: 'Log in', pl: 'Zaloguj się' },
  hadKey: {
    en: 'This device must be logged in again: the server no longer recognizes it (it may have been turned off in the parent panel, or the key is no longer valid). Enter the parent PIN — progress saved on this device will not be lost.',
    pl: 'To urządzenie trzeba zalogować jeszcze raz: serwer go już nie rozpoznaje (mogło zostać wyłączone w panelu rodzica albo klucz jest nieważny). Wpisz PIN rodzica — postęp zapisany na tym urządzeniu nie przepadnie.',
  },
  pinOnce: {
    en: 'You enter the PIN once on this device. After that your child only picks their profile — no passwords.',
    pl: 'PIN wpisuje się raz na urządzeniu. Potem dziecko wybiera już tylko swój profil — bez haseł.',
  },
  // „Kto dziś się uczy?”
  whoLearns: { en: "Who's learning today?", pl: 'Kto dziś się uczy?' },
  pickSub: { en: 'Pick a child’s profile to continue.', pl: 'Wybierz profil dziecka, aby kontynuować.' },
  addProfile: { en: 'Add profile', pl: 'Dodaj profil' },
  protectedByPin: { en: 'Protected with a PIN', pl: 'Chroniony PIN-em' },
  logOut: { en: 'Log out', pl: 'Wyloguj' },
  logOutTitle: { en: 'Log out of this device?', pl: 'Wylogować to urządzenie?' },
  logOutNote: {
    en: 'A parent has to enter the PIN. Afterwards the device must be logged in again with the parent PIN. Progress is saved on the server and does not get lost.',
    pl: 'Wymaga PIN-u rodzica. Potem urządzenie trzeba zalogować ponownie PIN-em rodzica. Postęp jest zapisany na serwerze i nie przepadnie.',
  },
  pickOffline: { en: '📴 No connection to the server — showing the profiles saved on this device.', pl: '📴 Brak połączenia z serwerem — pokazuję profile zapamiętane na tym urządzeniu.' },
  chooseProfile: { en: 'Choose a profile', pl: 'Wybierz profil' },
  loadingProgress: { en: 'Loading progress:', pl: 'Wczytuję postęp:' },
  // pierwszy profil
  firstBubble: { en: 'This device is logged in. Who will be learning?', pl: 'Urządzenie jest zalogowane. Kto będzie się uczyć?' },
  firstTitle: { en: 'First learner', pl: 'Pierwszy uczeń' },
  firstNote: {
    en: "A parent creates the child's profile. Each child has their own progress saved on the server — you can add more profiles on the “Who's learning today?” screen.",
    pl: 'Rodzic zakłada profil dziecka. Każde dziecko ma własny postęp zapisany na serwerze — kolejne profile dodasz na ekranie „Kto dziś się uczy?”.',
  },
  addBubble: { en: 'A new learner! What is their name?', pl: 'Nowy uczeń! Jak ma na imię?' },
  addTitle: { en: 'New profile', pl: 'Nowy profil' },
  addNote: {
    en: 'A parent creates the profile with the parent PIN. A PIN for the child is optional: with it, the profile asks for the PIN every time it is picked.',
    pl: 'Rodzic zakłada profil swoim PIN-em. PIN dziecka jest nieobowiązkowy: z nim profil przy każdym wyborze pyta o PIN.',
  },
  childPinHint: { en: "Child's PIN: 4 digits (optional)", pl: 'PIN dziecka: 4 cyfry (nieobowiązkowo)' },
  childPinLabel: { en: "Child's PIN", pl: 'PIN dziecka' },
  badChildPin: { en: "The child's PIN is exactly 4 digits (or leave it empty).", pl: 'PIN dziecka to dokładnie 4 cyfry (albo zostaw puste).' },
  back: { en: 'Back', pl: 'Wróć' },
  saveProfile: { en: 'Save profile', pl: 'Zapisz profil' },
  childNameHint: { en: "Child's name or nickname", pl: 'Imię albo pseudonim dziecka' },
  childName: { en: "Child's name", pl: 'Imię dziecka' },
  needPin: { en: 'Enter the parent PIN.', pl: 'Wpisz PIN rodzica.' },
  needName: { en: "Enter the child's name or nickname.", pl: 'Wpisz imię albo pseudonim dziecka.' },
  needGender: { en: 'Choose: boy or girl (the Polish forms in the exercises depend on it).', pl: 'Wybierz: chłopiec czy dziewczynka (od tego zależą formy w ćwiczeniach).' },
  saving: { en: 'Saving…', pl: 'Zapisuję…' },
  saveStart: { en: 'Save and start', pl: 'Zapisz i zacznij' },
  // brak internetu
  offlineBubble: { en: 'Oh no, I have no internet.', pl: 'Ojej, nie mam internetu.' },
  offlineNote: { en: '📴 Naative needs to connect to the internet once to load the profile. After that it also works offline.', pl: '📴 Naative musi raz połączyć się z internetem, żeby wczytać profil. Potem działa także bez sieci.' },
  tryAgain: { en: 'Try again', pl: 'Spróbuj ponownie' },
  // „Mój wygląd”
  myLook: { en: 'My look', pl: 'Mój wygląd' },
  done: { en: 'Done', pl: 'Gotowe' },
} as const satisfies Record<string, Pair>;

/** Komunikat dla rodzica po nieudanym parowaniu: po polsku to dokładnie `pairFailureText`, po angielsku odpowiednik. */
function pairFailure(r: Extract<PairResult, { ok: false }>, lang: 'en' | 'pl'): string {
  if (lang === 'pl') return pairFailureText(r);
  switch (r.reason) {
    case 'empty-pin':
      return 'Enter the parent PIN.';
    case 'bad-pin':
      return 'Wrong PIN. Try again.';
    case 'too-many':
      return `Too many wrong tries. Try again in about ${r.retryAfterMin ?? 15} min.`;
    case 'device-limit':
      return 'The server already has the maximum number of paired devices. One of them must be turned off in the parent panel (“Dla rodzica” ▸ Panel rodzica) — you open it with the same PIN.';
    case 'not-configured':
      return `The server is not set up yet${r.detail ? `: ${r.detail}` : '.'}`;
    case 'network':
      return 'No connection to the server. Check the internet and try again.';
    case 'no-server':
      return 'This address has no app server, so there is nothing to pair with (pairing works after deploying to Cloudflare; locally the keys in .env are enough).';
  }
}

const langCorner = renderLangCorner;

export interface ScreenDeps {
  createClient?: (pin: string) => AdminClient;
  deviceName?: () => string;
}

const account = (ctx: AppCtx): Accounts => {
  if (!ctx.account) throw new Error('Ekrany kont wymagają ctx.account.');
  return ctx.account;
};

/** PIN wpisany przed chwilą na ekranie logowania — żeby rodzic nie wpisywał go drugi raz przy zakładaniu pierwszego profilu. Tylko w pamięci; znika po użyciu. */
let rememberedPin = '';
export const forgetPin = (): void => {
  rememberedPin = '';
};

/** Maskotka z dymkiem (ekrany pomocnicze: łączenie, brak internetu, pierwszy profil). */
const mascotRow = (text: Child, pose: Pose = 'idle'): HTMLElement => h('div', { class: 'mascot-row' }, mascot(pose), h('div', { class: 'bubble' }, text));

/** Wpisanie Entera w polu = kliknięcie przycisku. */
function submitOnEnter(input: HTMLElement, run: () => void): void {
  input.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') run();
  });
}

// --- co pokazać po starcie ---------------------------------------------------------------------

export function renderLoading(ctx: AppCtx, text: Pair = T.connecting): void {
  ctx.root.replaceChildren(h('main', { class: 'stage account', 'data-testid': 'account-loading' }, mascotRow(tx(ctx, text))));
}

export function renderEntry(ctx: AppCtx, entry: Entry, deps: ScreenDeps = {}): void {
  switch (entry.screen) {
    case 'home':
      forgetPin();
      ctx.goHome();
      return;
    case 'login':
      renderLogin(ctx, { hadKey: entry.hadKey }, deps);
      return;
    case 'pick':
      forgetPin();
      renderPicker(ctx, entry, deps);
      return;
    case 'create-first':
      renderCreateFirst(ctx, deps);
      return;
    case 'offline':
      renderOffline(ctx);
      return;
  }
}

// --- logowanie ----------------------------------------------------------------------------------

export function renderLogin(ctx: AppCtx, opts: { hadKey: boolean }, deps: ScreenDeps = {}): void {
  const pin = txAttr(ctx, txAttr(ctx, h('input', { type: 'password', autocomplete: 'off', 'data-testid': 'login-pin' }), 'placeholder', T.pin), 'aria-label', T.pin);
  const defaultName = deps.deviceName?.() ?? (typeof navigator === 'undefined' ? 'Urządzenie' : guessDeviceName(navigator.userAgent, navigator.maxTouchPoints));
  const name = txAttr(ctx, txAttr(ctx, h('input', { type: 'text', maxlength: 40, autocomplete: 'off', value: defaultName, 'data-testid': 'login-name' }), 'aria-label', T.deviceName), 'placeholder', T.deviceNameHint);
  const message = h('p', { class: 'login-note', role: 'status', 'data-testid': 'login-message' });
  const status = liveStatus(ctx, message);
  let busy = false;
  const submit = async (): Promise<void> => {
    if (busy) return;
    busy = true;
    go.disabled = true;
    status.show((lang) => T.connecting[lang]);
    try {
      const r = await ctx.pair(pin.value, name.value);
      if (r.ok) {
        rememberedPin = pin.value.trim();
        pin.value = '';
        ctx.goHome();
        return;
      }
      status.show((lang) => `❌ ${pairFailure(r, lang)}`);
    } finally {
      busy = false;
      go.disabled = false;
    }
  };
  const go = h('button', { type: 'button', class: 'coral-btn', 'data-testid': 'login-submit', onclick: () => void submit() }, tx(ctx, T.logIn), icon('arrow'));
  submitOnEnter(pin, () => void submit());
  submitOnEnter(name, () => void submit());
  ctx.root.replaceChildren(
    h('main', { class: 'stage start-screen login', 'data-testid': 'login' },
      langCorner(ctx),
      mascot(LOGIN_SCREEN.pose),
      underlinedTitle(ctx, LOGIN_SCREEN.title),
      txText(ctx, h('p', { class: 'hand-text' }), LOGIN_SCREEN.text),
      h('div', { class: 'login-form' },
        opts.hadKey && txText(ctx, h('p', { class: 'login-note', 'data-testid': 'login-hadkey' }), T.hadKey),
        pin,
        name,
        go,
        message,
        txText(ctx, h('p', { class: 'login-note' }), T.pinOnce),
      ),
    ),
  );
}

// --- „Kto dziś się uczy?” ---------------------------------------------------------------------------

/**
 * Wybór profilu w stylu Netflixa (makieta patcha-34): duch z serduszkami, ręczny nagłówek, białe karty profili z kolorowym awatarem i kafelek „+ Dodaj profil”.
 * Pojawia się zawsze na świeżym starcie aplikacji; profil z PIN-em dziecka pyta o PIN (klawiatura w okienku). „Wyloguj” (PIN rodzica) odłącza urządzenie.
 */
export function renderPicker(ctx: AppCtx, entry: Extract<Entry, { screen: 'pick' }>, deps: ScreenDeps = {}): void {
  const acc = account(ctx);
  const status = h('p', { class: 'pick-status', role: 'status', 'data-testid': 'pick-status' }, entry.notice ?? '');
  let busy = false;
  const cards: HTMLButtonElement[] = [];

  const enter = (p: ProfileInfo): void => {
    if (busy) return;
    busy = true;
    for (const c of cards) c.disabled = true;
    status.textContent = `${tr(ctx, T.loadingProgress)} ${p.name}…`;
    void acc.select(p.id).then(() => ctx.goHome());
  };
  const choose = (p: ProfileInfo): void => {
    if (busy) return;
    if (!p.hasPin) return enter(p);
    const pad = openPinPad(ctx, {
      profile: p,
      verify: (pin) => acc.verifyPin(p.id, pin),
      onOk: () => {
        pad.close();
        enter(p);
      },
      onLost: () => ctx.goHome(), // profil usunięty albo urządzenie wylogowane: ekran startowy przelicza się od nowa
    });
  };

  for (const p of entry.profiles) {
    const lock = p.hasPin ? txAttr(ctx, h('span', { class: 'pc-lock', role: 'img', 'data-testid': 'pc-lock' }, icon('lock')), 'aria-label', T.protectedByPin) : null;
    const card = h('button', {
      type: 'button',
      class: `profile-card${p.hasPin ? ' locked' : ''}`,
      'data-testid': 'profile-card',
      'data-profile': p.id,
      'data-pin': p.hasPin ? 'yes' : 'no',
      onclick: () => choose(p),
    }, h('span', { class: 'pc-avatar' }, avatarEl(p, 'lg', ctx), lock), h('span', { class: 'pc-name' }, p.name));
    cards.push(card);
  }
  const add = entry.canAdd
    ? h('button', { type: 'button', class: 'profile-card add', 'data-testid': 'profile-add', onclick: () => renderCreateProfile(ctx, deps, 'add') }, h('span', { class: 'pc-plus' }, icon('plus')), txText(ctx, h('span', { class: 'pc-add' }), T.addProfile))
    : null;

  const logOut = h('button', {
    type: 'button',
    class: 'btn link logout',
    'data-testid': 'pick-logout',
    onclick: () => openParentGate(ctx, {
      title: T.logOutTitle,
      note: T.logOutNote,
      action: T.logOut,
      ...(deps.createClient ? { createClient: deps.createClient } : {}),
      onOk: () => {
        void ctx.unpair().then(() => {
          acc.signOut();
          ctx.goHome();
        });
      },
    }),
  }, icon('logout'), tx(ctx, T.logOut));

  ctx.root.replaceChildren(
    h('main', { class: 'stage start-screen picker', 'data-testid': 'picker' },
      langCorner(ctx),
      mascot('w-ready'),
      h('div', { class: 'pick-head' },
        txText(ctx, h('h1', { class: 'hand-title', 'data-testid': 'pick-title' }), T.whoLearns),
        txText(ctx, h('p', { class: 'pick-sub', 'data-testid': 'pick-sub' }), T.pickSub),
      ),
      entry.offline && txText(ctx, h('p', { class: 'login-note', 'data-testid': 'pick-offline' }), T.pickOffline),
      txAttr(ctx, h('div', { class: 'profile-grid picker-grid', role: 'group' }, ...cards, add), 'aria-label', T.chooseProfile),
      status,
      h('div', { class: 'picker-foot' },
        logOut,
        // Blok „Dla rodzica” zostaje po polsku (jak na ekranie głównym).
        h('details', { class: 'parent' }, h('summary', null, 'Dla rodzica'), button('🛠 Panel rodzica: profile, urządzenia i lekcje', () => renderAdmin(ctx), { class: 'btn ghost', testid: 'pick-admin' })),
      ),
    ),
  );
}

// --- nowy profil: pierwszy i kolejne ---------------------------------------------------------------

/** Pierwszy profil na świeżo zalogowanym urządzeniu (serwer nie ma jeszcze żadnego). */
export function renderCreateFirst(ctx: AppCtx, deps: ScreenDeps = {}): void {
  renderCreateProfile(ctx, deps, 'first');
}

/**
 * Formularz nowego profilu: PIN rodzica, imię, płeć, wygląd i (nieobowiązkowo) PIN dziecka. `first` — pierwszy profil na urządzeniu, po zapisaniu od razu się do niego wchodzi;
 * `add` — kolejny profil z kafelka „+ Dodaj profil”, po zapisaniu wracamy do wyboru profilu (dziecko wybiera swój kafelek).
 */
export function renderCreateProfile(ctx: AppCtx, deps: ScreenDeps = {}, mode: 'first' | 'add' = 'first'): void {
  const acc = account(ctx);
  const make = deps.createClient ?? ((pin: string) => createAdminClient(pin));
  const pin = txAttr(ctx, txAttr(ctx, h('input', { type: 'password', autocomplete: 'off', 'data-testid': 'first-pin' }), 'placeholder', T.pin), 'aria-label', T.pin);
  if (rememberedPin) pin.hidden = true; // PIN już wpisany na poprzednim ekranie
  const name = txAttr(ctx, txAttr(ctx, h('input', { type: 'text', maxlength: PROFILE_NAME_MAX, autocomplete: 'off', 'data-testid': 'first-name' }), 'placeholder', T.childNameHint), 'aria-label', T.childName);
  // PIN dziecka widać podczas pisania (rodzic ma go zapamiętać); wszystko poza cyframi jest od razu odrzucane.
  const childPin = txAttr(ctx, txAttr(ctx, h('input', { type: 'text', inputmode: 'numeric', pattern: '[0-9]*', maxlength: PIN_LENGTH, autocomplete: 'off', 'data-testid': 'first-child-pin' }), 'placeholder', T.childPinHint), 'aria-label', T.childPinLabel);
  const message = h('p', { class: 'muted', role: 'status', 'data-testid': 'first-message' });
  const status = liveStatus(ctx, message);
  const gender = createGenderPicker('', () => status.show(null), ctx);
  const picker = createLookPicker({ avatar: '', theme: 'neutral' }, (look) => applyLook(look), { name: () => name.value.trim(), ctx });
  name.addEventListener('input', () => status.show(null));
  childPin.addEventListener('input', () => {
    const digits = childPin.value.replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (digits !== childPin.value) childPin.value = digits;
    status.show(null);
  });
  let busy = false;
  const submit = async (): Promise<void> => {
    if (busy) return;
    const usePin = rememberedPin || pin.value.trim();
    if (!usePin) {
      status.show((lang) => T.needPin[lang]);
      return;
    }
    if (!name.value.trim()) {
      status.show((lang) => T.needName[lang]);
      return;
    }
    if (!gender.get()) {
      status.show((lang) => T.needGender[lang]);
      return;
    }
    const kidPin = childPin.value.trim();
    if (kidPin !== '' && !isPin(kidPin)) {
      status.show((lang) => T.badChildPin[lang]);
      return;
    }
    busy = true;
    go.disabled = true;
    status.show((lang) => T.saving[lang]);
    try {
      const look = picker.get();
      const r = await make(usePin).createProfile({ name: name.value.trim(), avatar: look.avatar, theme: look.theme, gender: gender.get(), ...(kidPin ? { pin: kidPin } : {}) });
      if (!r.ok) {
        if (r.kind === 'bad-pin') {
          rememberedPin = '';
          pin.hidden = false;
        }
        status.show(() => `❌ ${r.message}`); // komunikat z panelu rodzica zostaje po polsku
        return;
      }
      forgetPin();
      if (mode === 'first') await acc.adopt(r.data.profile);
      ctx.goHome(); // „add”: ekran startowy pobiera listę od nowa i pokazuje nowy kafelek
    } finally {
      busy = false;
      go.disabled = false;
    }
  };
  const go = txText(ctx, button('', () => void submit(), { class: 'btn primary big', testid: 'first-submit' }), mode === 'first' ? T.saveStart : T.saveProfile);
  submitOnEnter(name, () => void submit());
  submitOnEnter(childPin, () => void submit());
  const back = mode === 'add' ? txText(ctx, button('', () => ctx.goHome(), { class: 'btn link', testid: 'first-back' }), T.back) : null;
  ctx.root.replaceChildren(
    h('main', { class: 'stage account', 'data-testid': mode === 'first' ? 'create-first' : 'create-profile' },
      langCorner(ctx),
      mascotRow(tx(ctx, mode === 'first' ? T.firstBubble : T.addBubble)),
      h('div', { class: 'card account-card' },
        txText(ctx, h('h2'), mode === 'first' ? T.firstTitle : T.addTitle),
        txText(ctx, h('p', { class: 'muted' }), mode === 'first' ? T.firstNote : T.addNote),
        pin,
        name,
        gender.el,
        picker.el,
        childPin,
        go,
        message,
        back,
      ),
    ),
  );
}

// --- brak internetu ------------------------------------------------------------------------------

export function renderOffline(ctx: AppCtx): void {
  ctx.root.replaceChildren(
    h('main', { class: 'stage account', 'data-testid': 'account-offline' },
      langCorner(ctx),
      mascotRow(tx(ctx, T.offlineBubble), 'r-shrug'),
      h('div', { class: 'card account-card' },
        txText(ctx, h('p'), T.offlineNote),
        txText(ctx, button('', () => ctx.goHome(), { class: 'btn primary big', testid: 'offline-retry' }), T.tryAgain),
      ),
    ),
  );
}

// --- „Mój wygląd” (dziecko) -----------------------------------------------------------------------

/** Ekran „Mój wygląd”: awatar (trzy grupy), imię z ołówkiem (dziecko może je zmienić, gdy profil jest na serwerze) i „Gotowe”. Wybór awatara ustawia też motyw. */
export function renderLookScreen(ctx: AppCtx): void {
  const acc = account(ctx);
  ctx.leaveHome();
  ctx.at?.({ screen: 'look' });
  const profile: ProfileInfo | null = acc.profile();
  const picker = createLookPicker(
    acc.look(),
    (look: Look) => {
      acc.setLook(look);
      applyLook(look);
    },
    acc.mode() === 'family' ? { ctx, rename: { current: () => acc.profile()?.name ?? '', save: (n) => acc.rename(n, langOf(ctx)) } } : { ctx, name: () => profile?.name ?? '' },
  );
  ctx.root.replaceChildren(
    h('header', { class: 'topbar', 'data-kind': 'passive' },
      txAttr(ctx, button(icon('close'), () => ctx.goHome(), { class: 'icon-btn', testid: 'look-exit' }), 'title', T.done),
      h('div', { class: 'tb-mid' }, txText(ctx, h('div', { class: 'title' }), T.myLook)),
    ),
    h('main', { class: 'stage account look-screen', 'data-testid': 'look-screen' },
      h('div', { class: 'task' }, picker.el),
      txText(ctx, button('', () => ctx.goHome(), { class: 'btn primary big wide', testid: 'look-done' }), T.done),
    ),
  );
}
